import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { X, Users, Trash2, UserPlus, ShieldCheck, Eye, Copy, Check, Link2, Ban, CheckCircle2, AlertTriangle, Clock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatErr(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

function CopyField({ label, value, testid }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { /* ignore */ }
  };
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.15em] text-[#7C949C] mb-1">{label}</div>
      <div className="flex items-center gap-2">
        <code data-testid={`${testid}-value`} className="flex-1 mono text-sm text-[#0B2830] bg-[#F1F6F8] border border-[#D2E0E6] rounded-md px-3 py-2 break-all">{value}</code>
        <button data-testid={`${testid}-copy`} onClick={copy}
          className="shrink-0 bg-transparent border border-[#D2E0E6] text-[#47616B] p-2 rounded-md hover:bg-[#E0EBEF] hover:text-[#0B2830]">
          {copied ? <Check className="w-4 h-4 text-[#0D9488]" /> : <Copy className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
}

export default function UsersPanel({ onClose, token }) {
  const [users, setUsers] = useState([]);
  const [newEmail, setNewEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [created, setCreated] = useState(null); // { email, temp_password, emailed, email_error }
  const [resetInfo, setResetInfo] = useState(null); // { email, link, emailed, email_error }
  const authCfg = { headers: { Authorization: `Bearer ${token}` } };

  const load = useCallback(async () => {
    const res = await axios.get(`${API}/auth/users`, { headers: { Authorization: `Bearer ${token}` } });
    setUsers(res.data.users || []);
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const createUser = async () => {
    if (!newEmail.includes("@")) { setError("Please enter a valid email."); return; }
    setBusy(true); setError(null); setResetInfo(null);
    try {
      const res = await axios.post(`${API}/auth/users`, { email: newEmail.trim().toLowerCase(), base: window.location.origin }, authCfg);
      setCreated(res.data);
      setNewEmail("");
      load();
    } catch (e) {
      setError(formatErr(e.response?.data?.detail) || e.message);
    }
    setBusy(false);
  };

  const toggleActive = async (email, active) => {
    await axios.patch(`${API}/auth/users/${encodeURIComponent(email)}`, { active }, authCfg);
    load();
  };

  const removeUser = async (email) => {
    if (!window.confirm(`Remove ${email}? This permanently deletes their account.`)) return;
    await axios.delete(`${API}/auth/users/${encodeURIComponent(email)}`, authCfg);
    load();
  };

  const genResetLink = async (email) => {
    setCreated(null); setError(null);
    const res = await axios.post(`${API}/auth/users/${encodeURIComponent(email)}/reset-link`, { base: window.location.origin }, authCfg);
    setResetInfo({ email, ...res.data });
  };

  return (
    <div className="fixed inset-0 bg-[#0B2830]/70 flex items-center justify-center z-50 p-4" data-testid="users-panel">
      <div className="bg-white border border-[#D2E0E6] rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto fade-up">
        <div className="flex items-center justify-between border-b border-[#D2E0E6] px-5 py-4 sticky top-0 bg-white">
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-[#0284C7]" />
            <h3 className="font-semibold text-[#0B2830]">Manage users</h3>
          </div>
          <button data-testid="users-close" onClick={onClose} className="text-[#7C949C] hover:text-[#0B2830]">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {/* Create user */}
          <div className="bg-[#F1F6F8] border border-[#D2E0E6] rounded-md p-4">
            <label className="block text-[10px] uppercase tracking-[0.15em] text-[#7C949C] mb-2">Invite a read-only viewer</label>
            <div className="flex gap-2">
              <input data-testid="new-user-email" value={newEmail} type="email"
                onChange={(e) => setNewEmail(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && createUser()}
                placeholder="name@email.com"
                className="flex-1 bg-white border border-[#D2E0E6] text-[#0B2830] mono text-sm px-3 py-2 rounded-md focus:border-[#0284C7] outline-none" />
              <button data-testid="create-user-btn" onClick={createUser} disabled={busy}
                className="flex items-center gap-1.5 bg-[#0284C7] text-white px-4 rounded-md text-sm font-medium hover:bg-[#0369A1] disabled:opacity-50">
                <UserPlus className="w-4 h-4" /> {busy ? "Creating…" : "Create"}
              </button>
            </div>
            <p className="text-xs text-[#7C949C] mt-2">New accounts get a temporary password and read-only access to the screener.</p>
            {error && <div data-testid="users-error" className="mt-3 text-sm text-[#E11D48] border border-[#E11D48]/30 bg-[#E11D48]/5 rounded-md px-3 py-2">{error}</div>}
          </div>

          {/* Just-created credentials */}
          {created && (
            <div data-testid="created-box" className="border border-[#0D9488]/40 bg-[#0D9488]/5 rounded-md p-4 space-y-3">
              <div className="flex items-center gap-2 text-[#0D9488] text-sm font-medium">
                <CheckCircle2 className="w-4 h-4" /> Account created for {created.email}
              </div>
              <CopyField label="Temporary password" value={created.temp_password} testid="temp-pw" />
              <div className={`flex items-start gap-2 text-xs rounded-md px-3 py-2 ${created.emailed ? "text-[#0D9488]" : "text-[#B45309] bg-[#D97706]/5 border border-[#D97706]/30"}`}>
                {created.emailed ? <CheckCircle2 className="w-3.5 h-3.5 mt-0.5" /> : <AlertTriangle className="w-3.5 h-3.5 mt-0.5" />}
                <span data-testid="created-email-status">
                  {created.emailed
                    ? "The temporary password was emailed to the user."
                    : "Email couldn't be sent yet (domain not verified in Resend) — copy the password above and share it with the user directly."}
                </span>
              </div>
            </div>
          )}

          {/* Reset link output */}
          {resetInfo && (
            <div data-testid="reset-box" className="border border-[#0284C7]/40 bg-[#0284C7]/5 rounded-md p-4 space-y-3">
              <div className="flex items-center gap-2 text-[#0284C7] text-sm font-medium">
                <Link2 className="w-4 h-4" /> Password reset link for {resetInfo.email}
              </div>
              <CopyField label="One-time reset link (valid 1 hour)" value={resetInfo.link} testid="reset-link" />
              <div className={`flex items-start gap-2 text-xs rounded-md px-3 py-2 ${resetInfo.emailed ? "text-[#0D9488]" : "text-[#B45309] bg-[#D97706]/5 border border-[#D97706]/30"}`}>
                {resetInfo.emailed ? <CheckCircle2 className="w-3.5 h-3.5 mt-0.5" /> : <AlertTriangle className="w-3.5 h-3.5 mt-0.5" />}
                <span>{resetInfo.emailed ? "The reset link was emailed to the user." : "Email couldn't be sent yet — share the link above directly."}</span>
              </div>
            </div>
          )}

          {/* User list */}
          <div>
            <label className="block text-[10px] uppercase tracking-[0.15em] text-[#7C949C] mb-2">Accounts</label>
            <div className="space-y-2">
              {users.map((u) => (
                <div key={u.email} data-testid={`user-row-${u.email}`}
                  className="flex items-center justify-between gap-3 bg-white border border-[#D2E0E6] rounded-md px-3 py-2.5">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="mono text-sm text-[#0B2830] truncate">{u.email}</span>
                      {u.is_super ? (
                        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide bg-[#0284C7]/10 text-[#0284C7] px-1.5 py-0.5 rounded"><ShieldCheck className="w-3 h-3" /> Admin</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide bg-[#0D9488]/10 text-[#0D9488] px-1.5 py-0.5 rounded"><Eye className="w-3 h-3" /> Viewer</span>
                      )}
                      {!u.is_super && !u.active && (
                        <span className="text-[10px] uppercase tracking-wide bg-[#E11D48]/10 text-[#E11D48] px-1.5 py-0.5 rounded">Deactivated</span>
                      )}
                    </div>
                    <div data-testid={`last-login-${u.email}`} className="flex items-center gap-1 text-[11px] text-[#7C949C] mt-0.5">
                      <Clock className="w-3 h-3" />
                      {u.last_login
                        ? `Last sign-in ${new Date(u.last_login).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}`
                        : "Never signed in"}
                    </div>
                  </div>
                  {!u.is_super && (
                    <div className="flex items-center gap-1 shrink-0">
                      <button data-testid={`reset-link-${u.email}`} onClick={() => genResetLink(u.email)} title="Generate reset link"
                        className="text-[#7C949C] hover:text-[#0284C7] p-1.5"><Link2 className="w-4 h-4" /></button>
                      <button data-testid={`toggle-active-${u.email}`} onClick={() => toggleActive(u.email, !u.active)}
                        title={u.active ? "Deactivate" : "Reactivate"}
                        className={`p-1.5 ${u.active ? "text-[#7C949C] hover:text-[#D97706]" : "text-[#0D9488] hover:text-[#0f766e]"}`}>
                        {u.active ? <Ban className="w-4 h-4" /> : <CheckCircle2 className="w-4 h-4" />}
                      </button>
                      <button data-testid={`delete-user-${u.email}`} onClick={() => removeUser(u.email)} title="Remove"
                        className="text-[#7C949C] hover:text-[#E11D48] p-1.5"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
